/**
 * 신뢰도 시스템 API 서비스
 * 
 * 신뢰도 점수, 매칭, 리뷰 관련 API 호출을 담당합니다.
 * 
 * @author KUNKONG
 * @created 2026-01-20
 */

import axios from 'axios';
import { API_ORIGIN } from '../config/runtime';

// Axios 인스턴스 생성
const trustAPI = axios.create({
  baseURL: `${API_ORIGIN}/api/trust`,
  timeout: 10000,
});

// 요청 인터셉터: 인증 토큰 자동 추가
trustAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터: 에러 처리
trustAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Trust API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

/**
 * 신뢰도 점수 관련 API
 */
export const trustScoreAPI = {
  /**
   * 사용자 신뢰도 점수 조회
   * @param {number} userId - 사용자 ID
   * @returns {Promise} API 응답
   */
  getUserScore: async (userId: number) => {
    try {
      const response = await trustAPI.get(`/score/${userId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 신뢰도 점수 재계산
   * @param {number} userId - 사용자 ID
   * @returns {Promise} API 응답
   */
  recalculateScore: async (userId: number) => {
    try {
      const response = await trustAPI.post(`/recalculate/${userId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 신뢰도 순위 조회
   * @param {Object} params - 쿼리 파라미터
   * @param {string} params.category - 카테고리 (옵션)
   * @param {number} params.limit - 조회 개수 (기본값: 20)
   * @returns {Promise} API 응답
   */
  getRankings: async (params: { category?: string; limit?: number } = {}) => {
    try {
      const response = await trustAPI.get('/rankings', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

/**
 * 프로젝트 실적 관련 API
 */
export const achievementAPI = {
  /**
   * 프로젝트 완료 실적 등록
   * @param {Object} achievementData - 실적 데이터
   * @returns {Promise} API 응답
   */
  addAchievement: async (achievementData: any) => {
    try {
      const response = await trustAPI.post('/achievement', achievementData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

/**
 * 스마트 매칭 관련 API
 */
export const matchingAPI = {
  /**
   * 스마트 매칭 추천 조회
   * @param {number} projectId - 프로젝트 ID
   * @param {number} limit - 추천 개수 (기본값: 10)
   * @returns {Promise} API 응답
   */
  getSmartMatching: async (projectId: number, limit: number = 10) => {
    try {
      const response = await trustAPI.get(`/matching/${projectId}`, {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

/**
 * 강화된 리뷰 관련 API
 */
export const reviewAPI = {
  /**
   * 강화된 리뷰 작성
   * @param {Object} reviewData - 리뷰 데이터
   * @returns {Promise} API 응답
   */
  createReview: async (reviewData: any) => {
    try {
      const response = await trustAPI.post('/review', reviewData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

/**
 * 관리자 기능 API (관리자 전용)
 */
export const adminAPI = {
  /**
   * 모든 사용자 신뢰도 점수 일괄 재계산
   * @returns {Promise} API 응답
   */
  recalculateAllScores: async () => {
    try {
      const response = await trustAPI.post('/admin/recalculate-all');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 신뢰도 시스템 통계 조회
   * @returns {Promise} API 응답
   */
  getSystemStats: async () => {
    try {
      const response = await trustAPI.get('/admin/stats');
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

/**
 * 유틸리티 함수들
 */
export const trustUtils = {
  /**
   * 신뢰도 점수를 색상으로 변환
   * @param {number} score - 신뢰도 점수
   * @returns {string} 색상 코드
   */
  getScoreColor: (score: number): string => {
    if (score >= 90) return '#FFD700'; // 금색 (VIP)
    if (score >= 75) return '#FF6B6B'; // 빨간색 (프리미엄)
    if (score >= 60) return '#4ECDC4'; // 청록색 (검증)
    if (score >= 40) return '#45B7D1'; // 파란색 (신뢰)
    return '#95A5A6'; // 회색 (새싹)
  },

  /**
   * 신뢰도 등급을 한글 설명으로 변환
   * @param {string} level - 신뢰도 등급
   * @returns {string} 한글 설명
   */
  getLevelDescription: (level: string): string => {
    const descriptions = {
      'VIP': '최고 등급의 검증된 전문가',
      '프리미엄': '뛰어난 실력의 우수한 전문가',
      '검증': '신뢰할 수 있는 검증된 전문가',
      '신뢰': '기본적인 신뢰성을 갖춘 전문가',
      '새싹': '새로운 전문가 (신중한 선택 권장)'
    };
    return (descriptions as any)[level] || '등급 정보 없음';
  },

  /**
   * 매칭 점수를 백분율로 포맷
   * @param {number} score - 매칭 점수
   * @returns {string} 포맷된 백분율
   */
  formatMatchingScore: (score: number): string => {
    return `${Math.round(score)}%`;
  },

  /**
   * 프로젝트 금액을 포맷
   * @param {number} amount - 금액
   * @returns {string} 포맷된 금액
   */
  formatAmount: (amount: number): string => {
    if (amount >= 100000000) { // 1억 이상
      return `${(amount / 100000000).toFixed(1)}억원`;
    } else if (amount >= 10000000) { // 1천만 이상
      return `${Math.round(amount / 10000000)}천만원`;
    } else if (amount >= 1000000) { // 100만 이상
      return `${Math.round(amount / 1000000)}백만원`;
    } else if (amount >= 10000) { // 1만 이상
      return `${Math.round(amount / 10000)}만원`;
    } else {
      return `${amount.toLocaleString()}원`;
    }
  },

  /**
   * 날짜를 상대적 시간으로 포맷
   * @param {string|Date} date - 날짜
   * @returns {string} 상대적 시간
   */
  formatRelativeTime: (date: string | Date): string => {
    const now = new Date();
    const targetDate = new Date(date);
    const diffTime = Math.abs(now.getTime() - targetDate.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return '오늘';
    if (diffDays === 1) return '1일 전';
    if (diffDays < 7) return `${diffDays}일 전`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}주 전`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)}개월 전`;
    return `${Math.floor(diffDays / 365)}년 전`;
  },

  /**
   * 평점을 별점으로 변환
   * @param {number} rating - 평점 (1-5)
   * @param {boolean} showNumber - 숫자 표시 여부
   * @returns {string} 별점 문자열
   */
  formatRating: (rating: number | string, showNumber: boolean = true): string => {
    const numRating = typeof rating === 'string' ? parseFloat(rating) : rating;
    if (isNaN(numRating)) return '☆☆☆☆☆';
    
    const fullStars = Math.floor(numRating);
    const halfStar = numRating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);

    let stars = '★'.repeat(fullStars);
    if (halfStar) stars += '☆';
    stars += '☆'.repeat(emptyStars);

    return showNumber ? `${stars} (${numRating.toFixed(1)})` : stars;
  }
};

const trustAPIBundle = {
  trustScoreAPI,
  achievementAPI,
  matchingAPI,
  reviewAPI,
  adminAPI,
  trustUtils
};

export default trustAPIBundle;
